import React, { RefObject, useCallback, useEffect, useState } from 'react';
import styles from '../ReadStats.module.scss';
import { Table } from 'new/elements/Table';
import { Column } from 'new/elements/Table/types';
import { Stack, Typography } from '@mui/material';
import DateUtils from 'js/lib/DateUtils';
import FormatUtils from 'js/lib/FormatUtils';
import { useDrawerContext } from 'new/components/drawer/useDrawerContext';
import { fetchResourceCronHistory } from 'js/actions';
import { useDispatch } from 'react-redux';
import { Select } from 'new/elements/Select';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import {
  ResourceCronItem,
  useResourceCronHistory,
} from 'new/hooks/adapters/useResourceCronHistory';
import { RESOURCE_TYPE_SINK, RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { Pagination } from 'new/elements/Pagination';
import {
  cronSortByItemsSource,
  cronSortByItemsDestination,
  cronGroupByItems,
  cronSortByItems,
} from 'new/elements/ReadStats/constants';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface ItemFileProps {
  resourceType: string;
  resourceId: string;
  requestOnMount?: boolean;
  parentScrollRef?: RefObject<any>;
}

export const CronItems = (props: ItemFileProps) => {
  const { resourceId, resourceType, requestOnMount, parentScrollRef } = props;
  const showGroupBy = resourceType === RESOURCE_TYPE_SINK;
  const { items, meta, loading } = useResourceCronHistory(resourceType, resourceId);
  const [group, setGroup] = useState(
    showGroupBy ? meta.groupBy || cronGroupByItems?.[0]?.value : undefined,
  );
  const [sort, setSort] = useState(meta.orderBy || cronSortByItems[resourceType]?.[1]?.value);

  const dispatch = useAppDispatch();
  const { drawerContentRef } = useDrawerContext();

  useEffect(() => {
    if (requestOnMount) {
      requestFiles(1);
    }
  }, []);

  const requestFiles = useCallback(
    (page = 1) => {
      const filters = {
        page,
        size: 20,
        orderby: sort,
      };
      if (group) {
        filters['groupby'] = group;
      }
      dispatch(fetchResourceCronHistory(resourceId, resourceType, filters, null));
    },
    [resourceId, resourceType, sort, group],
  );

  const loadMore = useCallback(
    (page) => {
      requestFiles(page);
    },
    [resourceId, resourceType, sort, meta, group],
  );

  const onChangeSort = (e) => {
    setSort(e.target.value);
  };

  const onChangeGroup = (e) => {
    const { value } = e.target;
    setGroup(value);
    const currentSortOptions =
      cronSortByItems[
        group === cronGroupByItems[0].value ? RESOURCE_TYPE_SINK : RESOURCE_TYPE_SOURCE
      ];
    const index = currentSortOptions.findIndex((opt) => opt.value === sort);
    if (value === cronGroupByItems[0].value) {
      setSort(cronSortByItemsDestination[index].value);
    } else {
      setSort(cronSortByItemsSource[index].value);
    }
  };

  useDidUpdateEffect(() => {
    requestFiles(1);
  }, [requestFiles]);

  const onChangePage = (event, page) => {
    loadMore(page);
  };

  const { title, columns } = info[resourceType] || {};

  return (
    <div>
      <div className={styles.listFilters}>
        <Typography variant="subtitle1">{title}</Typography>
        <Stack direction="row" alignItems="center" gap="16px">
          {showGroupBy ? (
            <Select
              sx={{ minWidth: 150 }}
              items={cronGroupByItems}
              label="Group by"
              value={group}
              onChange={onChangeGroup}
              size="small"
            />
          ) : null}
          <Select
            sx={{ minWidth: 150 }}
            items={
              cronSortByItems[
                group === cronGroupByItems[0].value ? RESOURCE_TYPE_SINK : RESOURCE_TYPE_SOURCE
              ]
            }
            label="Sort by"
            value={sort}
            onChange={onChangeSort}
            size="small"
          />
        </Stack>
      </div>
      <Stack flex={1} height="100%">
        <Table
          parentScrollRef={parentScrollRef || drawerContentRef}
          totalCount={meta.totalCount}
          columns={columns}
          rows={items}
          loading={loading}
          emptyStateText="No history records found"
        />
      </Stack>
      {!meta.pageCount || meta.pageCount === 1 ? null : (
        <Stack className={styles.pagination}>
          <Pagination
            count={meta.pageCount}
            shape="rounded"
            color="primary"
            onChange={onChangePage}
          />
        </Stack>
      )}
    </div>
  );
};

const columns = [
  {
    name: 'Time',
    field: 'runIdTime',
    renderRow(row) {
      return DateUtils.getDateString(row?.runIdTime, 'MMM D, YYYY - hh:mmA z');
    },
  },
  {
    name: 'Records',
    field: 'recordCount',
  },
  {
    name: 'Size',
    sortable: true,
    field: 'size',
    renderRow(row) {
      return FormatUtils.bytesToString(row.size);
    },
  },
  {
    name: 'Error',
    sortable: true,
    field: 'errorCount',
  },
  {
    name: 'Detected dataset',
    field: 'dataSetId',
  },
] as Column<ResourceCronItem>[];

const info = {
  [RESOURCE_TYPE_SINK]: {
    title: 'Data write history',
    columns: columns,
  },
  [RESOURCE_TYPE_SOURCE]: {
    title: 'Ingestion history',
    columns,
  },
};
